import { useGet } from '../../../../hooks/useApi';
import { Container } from 'react-bootstrap';
import OnError from '../../../../components/OnError';
import { Button, Skeleton, Progress, Typography, Alert, Result } from 'antd';
import React, { useEffect, useState, useCallback } from 'react';
import { DocusealForm } from '@docuseal/react';
import { useGet as useGetLocalSchedule } from '../../../../hooks/localSchedule/useGet';
import { useClear as useClearLocalSchedule } from '../../../../hooks/localSchedule/useClear';
import NextStepButton from '../../components/NextStepButton';
import Complete from '../Complete';
import { Pb3 } from '../../../../components/P';

const { Title, Text } = Typography;

interface ContractData {
  studentId: number;
  studentName: string;
  documentId: string;
  slug: string;
  completed: boolean;
}

interface ContractResolution {
  contracts: ContractData[];
  requiresSignature: boolean;
  canComplete: boolean;
}

interface SignDocumentationProps {
  paymentIntentId: string;
  onGoBack: () => void;
}

const SignDocumentation = ({
  paymentIntentId,
  onGoBack,
}: SignDocumentationProps) => {
  const urlParams = new URLSearchParams(window.location.search);
  const redirectStatus = urlParams.get('redirect_status') || 'succeeded';
  const localSchedule = useGetLocalSchedule(true);
  const clearLocalSchedule = useClearLocalSchedule();
  // Stripe sends `processing` for payments that settle later, such as bank
  // debits. The payment went through; contracts follow once it settles.
  const isPaymentProcessing = redirectStatus === 'processing';

  // State to track the current contract index and completion
  const [currentContractIndex, setCurrentContractIndex] = useState<number>(0);
  const [allContractsCompleted, setAllContractsCompleted] =
    useState<boolean>(false);
  // This is a guard to prevent repeated calls from Docuseal's onComplete
  const [formJustCompleted, setFormJustCompleted] = useState<boolean>(false);
  // State for handling contract generation waiting
  const [retryCount, setRetryCount] = useState<number>(0);

  // Retrieve all contract slugs for this paymentIntent
  const getContractSlugs = useGet<ContractResolution>(
    `/v1/auth/reg/register-schedule/contract-slugs/${paymentIntentId}`
  );

  const contracts = getContractSlugs.data?.contracts || [];
  const canCompleteWithoutContracts = !!getContractSlugs.data?.canComplete;

  // Determine which contract to show first by finding the first incomplete one
  useEffect(() => {
    if (contracts.length > 0) {
      const incompleteIndex = contracts.findIndex(
        (contract) => !contract.completed
      );
      if (incompleteIndex !== -1) {
        setCurrentContractIndex(incompleteIndex);
      } else {
        // If all contracts are already complete
        setAllContractsCompleted(true);
      }
    }
  }, [contracts]);

  // Clear localSchedule once the documentation (and scheduling) has loaded successfully
  useEffect(() => {
    if (
      !getContractSlugs.isLoading &&
      !getContractSlugs.isError &&
      (redirectStatus === 'succeeded' || isPaymentProcessing) &&
      localSchedule
    ) {
      clearLocalSchedule();
    }
  }, [
    getContractSlugs.isLoading,
    getContractSlugs.isError,
    redirectStatus,
    isPaymentProcessing,
    localSchedule,
    clearLocalSchedule,
  ]);

  // Polling effect for waiting for contracts to be generated
  useEffect(() => {
    // For fake payment intents (fk_*), use shorter retry interval
    // since they should be processed immediately without Stripe
    const isFakePayment = paymentIntentId.startsWith('fk_');
    const retryInterval = isFakePayment ? 1000 : 3000; // 1s for fake, 3s for real

    if (
      redirectStatus === 'succeeded' &&
      !getContractSlugs.isLoading &&
      !getContractSlugs.isError &&
      contracts.length === 0 &&
      !canCompleteWithoutContracts &&
      retryCount < 20 // Maximum 20 retries
    ) {
      const timer = setTimeout(() => {
        console.log(
          `Retrying contract fetch attempt ${retryCount + 1}/20 ${isFakePayment ? '(zero-payment registration)' : '(paid registration)'}`
        );
        getContractSlugs.refetch();
        setRetryCount((prev) => prev + 1);
      }, retryInterval);

      return () => clearTimeout(timer);
    } else if (contracts.length > 0) {
      setRetryCount(0);
    }
  }, [
    redirectStatus,
    getContractSlugs.isLoading,
    getContractSlugs.isError,
    getContractSlugs.data,
    retryCount,
    getContractSlugs,
    paymentIntentId,
    contracts.length,
    canCompleteWithoutContracts,
  ]);

  const handleOnGoBack = () => {
    onGoBack();
  };

  // Reset the form-completion guard each time we successfully move to a new contract
  useEffect(() => {
    setFormJustCompleted(false);
  }, [currentContractIndex]);

  // Callback triggered once a Docuseal form is completed
  const handleFormCompleted = useCallback(async () => {
    // If we’ve already handled a completion event for this same contract, skip
    if (formJustCompleted) return;
    setFormJustCompleted(true);

    try {
      const response = await getContractSlugs.refetch();
      const updatedContracts = response.data?.contracts || [];

      if (updatedContracts.length === 0) {
        if (response.data?.canComplete) {
          setAllContractsCompleted(true);
        }
        return;
      }

      // Find the first incomplete contract’s index
      const nextIncompleteIndex = updatedContracts.findIndex(
        (contract) => !contract.completed
      );

      if (nextIncompleteIndex === -1) {
        // All are complete
        setAllContractsCompleted(true);
      } else {
        // Move to the next incomplete
        setCurrentContractIndex(nextIncompleteIndex);
      }
    } catch (error) {
      console.error('Error refreshing contract data:', error);
      // The refresh failed, so use the contracts already loaded. DocuSeal just
      // reported the current one as signed, so move to the next unsigned one
      // after it.
      const loadedContracts = getContractSlugs.data?.contracts || [];
      const nextIncompleteIndex = loadedContracts.findIndex(
        (contract, index) => index > currentContractIndex && !contract.completed
      );
      if (nextIncompleteIndex === -1) {
        setAllContractsCompleted(true);
      } else {
        setCurrentContractIndex(nextIncompleteIndex);
      }
    }
  }, [getContractSlugs, formJustCompleted, currentContractIndex]);

  if (isPaymentProcessing) {
    return (
      <Container>
        <Result
          status="info"
          title="Payment Processing"
          subTitle="Your payment was submitted and is still processing. We'll email you when it completes. You can then sign your contracts from the scheduling page."
          extra={
            <Button
              type="primary"
              onClick={() => (window.location.href = '/scheduling')}
            >
              Return to Scheduling
            </Button>
          }
        />
      </Container>
    );
  }

  // If payment didn't succeed
  if (redirectStatus !== 'succeeded') {
    return (
      <Container>
        <OnError
          title="Error"
          status={500}
          subTitle="Something went wrong with your payment"
          extra={
            <Button type="default" onClick={handleOnGoBack}>
              Go Back
            </Button>
          }
        />
      </Container>
    );
  }

  // While loading contract data
  if (getContractSlugs.isLoading) {
    return (
      <Container>
        <div role="progressbar">
          <Skeleton active />
        </div>
      </Container>
    );
  }

  // If there's an error loading contract data
  if (getContractSlugs.isError) {
    return (
      <Container>
        <OnError
          title="Error Loading Documents"
          status={500}
          subTitle="Unable to load contract documents"
          extra={
            <Button type="default" onClick={handleOnGoBack}>
              Go Back
            </Button>
          }
        />
      </Container>
    );
  }

  // Contracts array
  if (canCompleteWithoutContracts && contracts.length === 0) {
    return <Complete paymentIntentId={paymentIntentId} />;
  }

  if (contracts.length === 0) {
    if (urlParams.get('redirect_status') === 'succeeded') {
      // Se excedeu o número máximo de tentativas
      if (retryCount >= 20) {
        const isFakePayment = paymentIntentId.startsWith('fk_');

        return (
          <Container>
            <Pb3>
              <Alert
                message="Contract Generation Taking Longer Than Expected"
                description={
                  <div>
                    <p>
                      {isFakePayment
                        ? "Your registration was successful, but we're experiencing a delay in generating your contracts."
                        : "Your payment was successful, but we're experiencing a delay in generating your contracts."}
                    </p>
                    <p>
                      <strong>Don&apos;t worry</strong> - your registration is
                      secure and will be processed.
                    </p>
                    <p>
                      Please try refreshing the page, or contact support if the
                      issue persists.
                    </p>
                    <p>
                      <strong>
                        {isFakePayment ? 'Registration' : 'Payment Intent'} ID:
                      </strong>{' '}
                      {paymentIntentId}
                    </p>
                  </div>
                }
                type="warning"
                showIcon
                action={
                  <div style={{ marginTop: 16 }}>
                    <Button
                      type="primary"
                      onClick={() => window.location.reload()}
                      style={{ marginRight: 8 }}
                    >
                      Refresh Page
                    </Button>
                    <Button
                      onClick={() => {
                        window.location.href = '/scheduling';
                      }}
                    >
                      Return to Dashboard
                    </Button>
                  </div>
                }
              />
            </Pb3>
          </Container>
        );
      }

      // Pagamento bem-sucedido mas contratos ainda não gerados
      // Implementar polling para aguardar geração de contratos
      return (
        <Container>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Progress
              type="circle"
              percent={Math.min(95, (retryCount / 20) * 100 + 75)}
              status="active"
              format={() => '🔄'}
            />
            <Title level={3} style={{ marginTop: 24 }}>
              Preparing Your Contracts
            </Title>
            <Text
              type="secondary"
              style={{ fontSize: '16px', display: 'block', marginBottom: 24 }}
            >
              Your payment was successful! We&apos;re now generating your
              registration contracts.
              <br />
              This process typically takes a few moments. Please wait...
              {retryCount > 0 && (
                <span
                  style={{ display: 'block', marginTop: 8, fontSize: '14px' }}
                >
                  Attempt {retryCount}/20 - Checking for contracts...
                </span>
              )}
            </Text>

            <Alert
              message="What's happening?"
              description="We're creating your personalized registration contracts based on your payment confirmation. Once ready, you'll be able to review and sign them electronically."
              type="info"
              showIcon
              style={{ textAlign: 'left', marginTop: 24 }}
            />
          </div>
        </Container>
      );
    }

    return (
      <Container>
        <Pb3>
          <Alert
            message="No Contracts Found"
            description="No contracts were found for this registration. Please contact support if you believe this is an error."
            type="warning"
            showIcon
          />
        </Pb3>
        <NextStepButton
          text="Return to Dashboard"
          disabled={false}
          onClick={() => (window.location.href = '/scheduling')}
        />
      </Container>
    );
  }

  // If all contracts have been completed
  if (allContractsCompleted) {
    return (
      <Container>
        <Pb3>
          <Alert
            message="All Contracts Signed"
            description="Thank you! All contracts have been successfully signed and submitted."
            type="success"
            showIcon
          />
        </Pb3>
        <Complete paymentIntentId={paymentIntentId} />
      </Container>
    );
  }

  const currentContract = contracts[currentContractIndex];

  // Safety check to ensure a current contract exists
  if (!currentContract) {
    return (
      <Container>
        <div role="progressbar">
          <Skeleton active />
          <p>Loading contract information...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div style={{ marginBottom: 20 }}>
        <Progress
          percent={Math.round(
            ((currentContractIndex + 1) / contracts.length) * 100
          )}
          status="active"
        />
        <Title level={4} style={{ marginTop: 16 }}>
          Contract {currentContractIndex + 1}/{contracts.length} -{' '}
          {currentContract.studentName}
        </Title>
        <Text type="secondary">
          Please review and sign this contract for {currentContract.studentName}
          .
        </Text>
      </div>

      <div
        style={{
          maxHeight: '800px',
          overflow: 'auto',
          border: '1px solid #f0f0f0',
          borderRadius: '4px',
        }}
      >
        {currentContract.slug && (
          <DocusealForm
            key={currentContract.slug}
            src={`https://docuseal.com/s/${currentContract.slug}`}
            onComplete={handleFormCompleted}
          />
        )}
      </div>
    </Container>
  );
};

export default SignDocumentation;
